import React, { useState } from 'react';
import { CheckCircle2, Trophy, MonitorPlay, FileText } from 'lucide-react';
type Level = 'Junior' | 'Mid-Level' | 'Senior' | 'Artisan';
interface Feature {
  name: string;
  included: boolean;
}
interface PricingCardData {
  title: string;
  icon: React.ReactNode;
  price: number;
  originalPrice?: number;
  discount?: string;
  bestValue?: boolean;
  buyLink?: string;
  features: Feature[];
}
const levelsData: Record<Level, {
  description: string;
  accentColor: string;
  badgeLabel?: string;
  cards: PricingCardData[];
}> = {
  Junior: {
    description: 'The Junior Laravel Developer certification validates entry-level skills, providing a solid foundation for newcomers or those formalizing their basic knowledge, with opportunities to pursue higher-level certifications as their career progresses.',
    accentColor: 'text-laravel-junior',
    cards: [{
      title: 'Exam Only',
      icon: <FileText className="w-5 h-5 text-laravel-junior" data-id="element-128" />,
      price: 69,
      buyLink: 'https://certificationforlaravel.quadernoapp.com/checkout/link/0e434f04ec349a899cdbccd8c43defc3c3f675a8',
      features: [{
        name: 'Preparation Guide Access',
        included: true
      }, {
        name: 'Discord Community Access',
        included: true
      }, {
        name: 'Junior Laravel Exam Voucher',
        included: true
      }]
    }, {
      title: 'Exam & Preparation bundle',
      icon: <Trophy className="w-5 h-5 text-laravel-junior" data-id="element-129" />,
      price: 99,
      bestValue: true,
      buyLink: 'https://certificationforlaravel.quadernoapp.com/checkout/link/64a4de3f918ed5ce17c999b3bf1879d083b31105',
      features: [{
        name: 'Preparation Guide Access',
        included: true
      }, {
        name: 'Discord Community Access',
        included: true
      }, {
        name: 'Junior Laravel Exam Voucher',
        included: true
      }, {
        name: 'Self-Study Training Access',
        included: true
      }, {
        name: 'Training Challenge Solutions',
        included: true
      }, {
        name: 'Free Retake',
        included: true
      }]
    }]
  },
  'Mid-Level': {
    description: 'The Mid-Level Laravel Developer certification bridges the gap between junior and senior expertise, validating your advanced skills and preparing you for more senior-level challenges.',
    accentColor: 'text-laravel-mid',
    cards: [{
      title: 'Exam Only',
      icon: <FileText className="w-5 h-5 text-laravel-mid" data-id="element-130" />,
      price: 170,
      originalPrice: 179,
      discount: '5% OFF',
      buyLink: 'https://certificationforlaravel.quadernoapp.com/checkout/link/86fefbb3fa2bde77ccaafaab1d3e1d33ab33061f',
      features: [{
        name: 'Preparation Guide Access',
        included: true
      }, {
        name: 'Discord Community Access',
        included: true
      }, {
        name: 'Mid-Level Laravel Exam Voucher',
        included: true
      }]
    }, {
      title: 'Exam & Preparation bundle',
      icon: <Trophy className="w-5 h-5 text-laravel-mid" data-id="element-131" />,
      price: 340,
      originalPrice: 378,
      discount: '10% OFF',
      bestValue: true,
      buyLink: 'https://certificationforlaravel.quadernoapp.com/checkout/link/b02034e06de77561baa5611af92840c24349be26',
      features: [{
        name: 'Preparation Guide Access',
        included: true
      }, {
        name: 'Discord Community Access',
        included: true
      }, {
        name: 'Self-Study Training Access',
        included: true
      }, {
        name: 'Training Challenge Solutions',
        included: true
      }, {
        name: 'Free Retake',
        included: true
      }, {
        name: 'Mid-Level Laravel Exam Voucher',
        included: true
      }]
    }, {
      title: 'Exam, Preparation & Bootcamp bundle',
      icon: <MonitorPlay className="w-5 h-5 text-laravel-mid" data-id="element-132" />,
      price: 599,
      originalPrice: 999,
      discount: '40% OFF',
      buyLink: 'https://certificationforlaravel.quadernoapp.com/checkout/link/afb6d61d835c2c9016542758c5802a1f7d6c6320',
      features: [{
        name: 'Preparation Guide Access',
        included: true
      }, {
        name: 'Discord Community Access',
        included: true
      }, {
        name: 'Self-Study Training Access',
        included: true
      }, {
        name: 'Training Challenge Solutions',
        included: true
      }, {
        name: 'Mid-Level Laravel Exam Voucher',
        included: true
      }, {
        name: 'Free Retakes',
        included: true
      }, {
        name: 'Intensive Bootcamp Training',
        included: true
      }]
    }]
  },
  Senior: {
    description: 'This certification verifies your ability to lead Laravel projects, architect enterprise-level applications, and implement advanced patterns including service containers, custom packages, and performance optimization.',
    accentColor: 'text-laravel-senior',
    cards: [{
      title: 'Exam Only',
      icon: <FileText className="w-5 h-5 text-laravel-senior" data-id="element-133" />,
      price: 209,
      originalPrice: 219,
      discount: '5% OFF',
      buyLink: 'https://certificationforlaravel.quadernoapp.com/checkout/link/c822dedaba3b17f2c944d28b26f8b99d8ed8fe92',
      features: [{
        name: 'Preparation Guide Access',
        included: true
      }, {
        name: 'Discord Community Access',
        included: true
      }, {
        name: 'Senior Laravel Exam Voucher',
        included: true
      }]
    }, {
      title: 'Exam & Preparation bundle',
      icon: <Trophy className="w-5 h-5 text-laravel-senior" data-id="element-134" />,
      price: 359,
      originalPrice: 418,
      discount: '14% OFF',
      bestValue: true,
      buyLink: 'https://certificationforlaravel.quadernoapp.com/checkout/link/1d33255987bb76fa49a2d60a96ea5fa21608471e',
      features: [{
        name: 'Preparation Guide Access',
        included: true
      }, {
        name: 'Discord Community Access',
        included: true
      }, {
        name: 'Self-Study Training Access',
        included: true
      }, {
        name: 'Training Challenge Solutions',
        included: true
      }, {
        name: 'Free Retake',
        included: true
      }, {
        name: 'Senior Laravel Exam Voucher',
        included: true
      }]
    }, {
      title: 'Exam, Preparation & Bootcamp bundle',
      icon: <MonitorPlay className="w-5 h-5 text-laravel-senior" data-id="element-135" />,
      price: 699,
      originalPrice: 1167,
      discount: '40% OFF',
      buyLink: 'https://certificationforlaravel.quadernoapp.com/checkout/link/0457130fe59d2f369966b0d91e2cbd192a78f11f',
      features: [{
        name: 'Preparation Guide Access',
        included: true
      }, {
        name: 'Discord Community Access',
        included: true
      }, {
        name: 'Self-Study Training Access',
        included: true
      }, {
        name: 'Training Challenge Solutions',
        included: true
      }, {
        name: 'Senior Laravel Exam Voucher',
        included: true
      }, {
        name: 'Free Retakes',
        included: true
      }, {
        name: 'Intensive Bootcamp Training',
        included: true
      }]
    }]
  },
  Artisan: {
    description: 'The Artisan Laravel Developer package includes both Senior-level and Artisan certifications, providing the most comprehensive and cost-effective path for your Laravel career. While you can upgrade from Senior-level to Artisan later, investing in the full package from the start offers the best value and a seamless progression to advanced expertise.',
    accentColor: 'text-laravel-artisan',
    badgeLabel: 'Senior +\nArtisan',
    cards: [{
      title: 'Exam Only',
      icon: <FileText className="w-5 h-5 text-laravel-artisan" data-id="element-136" />,
      price: 599,
      originalPrice: 718,
      discount: '17% OFF',
      buyLink: 'https://certificationforlaravel.quadernoapp.com/checkout/link/5d09c1b0be9487669d32690c23ccc963be395d88',
      features: [{
        name: 'Preparation Guide Access',
        included: true
      }, {
        name: 'Discord Community Access',
        included: true
      }, {
        name: 'Senior Laravel Exam Voucher',
        included: true
      }, {
        name: 'Artisan Laravel Exam Voucher',
        included: true
      }]
    }, {
      title: 'Exam & Preparation bundle',
      icon: <Trophy className="w-5 h-5 text-laravel-artisan" data-id="element-137" />,
      price: 849,
      originalPrice: 1166,
      discount: '27% OFF',
      bestValue: true,
      buyLink: 'https://certificationforlaravel.quadernoapp.com/checkout/link/0a383c5894cda74a820fbcf32ac7204ccf549ca4',
      features: [{
        name: 'Preparation Guide Access',
        included: true
      }, {
        name: 'Discord Community Access',
        included: true
      }, {
        name: 'Self-Study Training Access',
        included: true
      }, {
        name: 'Training Challenge Solutions',
        included: true
      }, {
        name: 'Free Retake',
        included: true
      }, {
        name: 'Senior Laravel Exam Voucher',
        included: true
      }, {
        name: 'Artisan Laravel Exam Voucher',
        included: true
      }]
    }, {
      title: 'Exam, Preparation & Bootcamp bundle',
      icon: <MonitorPlay className="w-5 h-5 text-laravel-artisan" data-id="element-138" />,
      price: 1499,
      originalPrice: 1915,
      discount: '22% OFF',
      buyLink: 'https://certificationforlaravel.quadernoapp.com/checkout/link/a6f44a0ee7837b59fd7383264959e64f99536943',
      features: [{
        name: 'Preparation Guide Access',
        included: true
      }, {
        name: 'Discord Community Access',
        included: true
      }, {
        name: 'Self-Study Training Access',
        included: true
      }, {
        name: 'Training Challenge Solutions',
        included: true
      }, {
        name: 'Senior Laravel Exam Voucher',
        included: true
      }, {
        name: 'Artisan Laravel Exam Voucher',
        included: true
      }, {
        name: 'Free Retakes',
        included: true
      }, {
        name: 'Intensive Bootcamp Training',
        included: true
      }]
    }]
  }
};
export function LevelPricing() {
  const [activeTab, setActiveTab] = useState<Level>('Junior');
  const activeData = levelsData[activeTab];
  const getTabColor = (level: Level) => {
    if (activeTab === level) {
      switch (level) {
        case 'Junior':
          return 'bg-laravel-junior text-white shadow-md';
        case 'Mid-Level':
          return 'bg-laravel-mid text-white shadow-md';
        case 'Senior':
          return 'bg-laravel-senior text-white shadow-md';
        case 'Artisan':
          return 'bg-laravel-artisan text-white shadow-md';
        default:
          return 'bg-laravel-red text-white shadow-md';
      }
    }
    switch (level) {
      case 'Junior':
        return 'bg-white text-laravel-junior hover:bg-yellow-50 border border-laravel-junior/30';
      case 'Mid-Level':
        return 'bg-white text-laravel-mid hover:bg-orange-50 border border-laravel-mid/30';
      case 'Senior':
        return 'bg-white text-laravel-senior hover:bg-purple-50 border border-laravel-senior/30';
      case 'Artisan':
        return 'bg-white text-laravel-artisan hover:bg-red-50 border border-laravel-artisan/30';
      default:
        return 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200';
    }
  };
  const getBorderColor = (level: Level) => {
    switch (level) {
      case 'Junior':
        return 'border-laravel-junior';
      case 'Mid-Level':
        return 'border-laravel-mid';
      case 'Senior':
        return 'border-laravel-senior';
      case 'Artisan':
        return 'border-laravel-artisan';
      default:
        return 'border-gray-200';
    }
  };
  const getButtonColor = (level: Level) => {
    switch (level) {
      case 'Junior':
        return 'bg-laravel-junior text-white hover:bg-yellow-600';
      case 'Mid-Level':
        return 'bg-laravel-mid text-white hover:bg-yellow-600';
      case 'Senior':
        return 'bg-laravel-senior text-white hover:bg-purple-700';
      case 'Artisan':
        return 'bg-laravel-artisan text-white hover:bg-red-600';
      default:
        return 'bg-gray-900 text-white';
    }
  };
  const getOutlineButtonColor = (level: Level) => {
    switch (level) {
      case 'Junior':
        return 'border-2 border-laravel-junior text-laravel-junior hover:bg-laravel-junior hover:text-white';
      case 'Mid-Level':
        return 'border-2 border-laravel-mid text-laravel-mid hover:bg-laravel-mid hover:text-white';
      case 'Senior':
        return 'border-2 border-laravel-senior text-laravel-senior hover:bg-laravel-senior hover:text-white';
      case 'Artisan':
        return 'border-2 border-laravel-artisan text-laravel-artisan hover:bg-laravel-artisan hover:text-white';
      default:
        return 'border-2 border-laravel-red text-laravel-red hover:bg-laravel-red hover:text-white';
    }
  };
  const getCheckmarkBg = (level: Level) => {
    switch (level) {
      case 'Junior':
        return 'bg-laravel-junior';
      case 'Mid-Level':
        return 'bg-laravel-mid';
      case 'Senior':
        return 'bg-laravel-senior';
      case 'Artisan':
        return 'bg-laravel-artisan';
      default:
        return 'bg-laravel-red';
    }
  };
  return <div className="w-full max-w-6xl mx-auto px-4 py-12 md:py-20" data-id="element-139">
      {/* Certificates.dev notice */}
      <div className="flex flex-col items-center gap-3 mb-14" data-id="element-140">
        <img src="https://cdn.magicpatterns.com/uploads/bWfr9sxTo1Jvak7FxoTFBr/asset_image.png" alt="certificates.dev" className="h-8 w-auto" data-id="element-141" />
        <p className="text-gray-500 text-sm text-center" data-id="element-142">
          All our exams are managed and hosted on the certificates.dev platform
        </p>
      </div>

      <div className="text-center mb-8 md:mb-12" data-id="element-143">
        <h2 className="text-3xl md:text-5xl font-display font-bold mb-8 md:mb-12 text-gray-900" data-id="element-144">
          Choose Your <span className="text-laravel-red" data-id="element-145">Level</span>
        </h2>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-1.5 md:gap-2 mb-8 p-1.5 md:p-2 rounded-full max-w-3xl mx-auto bg-gray-100 border border-gray-200" data-id="element-146">
          {(Object.keys(levelsData) as Level[]).map(level => <button key={level} onClick={() => setActiveTab(level)} className={`flex-1 min-w-[75px] md:min-w-[120px] py-2.5 md:py-3 px-3 md:px-6 rounded-full font-bold text-xs md:text-sm transition-all duration-300 ${getTabColor(level)}`} data-id="element-147">
              {level}
            </button>)}
        </div>

        <p className="text-gray-600 max-w-4xl mx-auto text-sm md:text-base leading-relaxed mb-16" data-id="element-148">
          {activeData.description}
        </p>
      </div>

      {/* Pricing Cards */}
      <div className={`flex flex-col items-center md:flex-row md:justify-center md:items-stretch gap-6 rounded-3xl`} data-id="element-149">
        {activeData.cards.map((card, index) => <div key={index} className={`relative flex flex-col bg-card border rounded-3xl p-6 md:p-8 flex-1 max-w-sm w-full transition-transform duration-300 hover:-translate-y-2 bg-white
              ${card.bestValue ? `border-2 ${getBorderColor(activeTab)} z-10 shadow-2xl` : 'border-gray-200 shadow-md'}
            `} data-id="element-150">
            {card.bestValue && <div className="absolute top-0 left-0 right-0 -mt-4 flex justify-center" data-id="element-151">
                <span className={`text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider ${getButtonColor(activeTab)}`} data-id="element-152">
                  BEST VALUE
                </span>
              </div>}

            {activeTab === 'Junior' && <div className="absolute -top-4 -right-4 w-20 h-20 drop-shadow-lg" data-id="element-153">
                <div className="relative w-full h-full rounded-full bg-laravel-junior/10 border-2 border-laravel-junior flex items-center justify-center" data-id="element-154">
                  <img src="https://cdn.magicpatterns.com/uploads/dSUW2txnMjoSYFwnCy5Pwi/Junior_Laravel_Developer.png" alt="Junior Laravel Developer Badge" className="w-16 h-16 object-contain" data-id="element-155" />
                </div>
              </div>}

            {activeTab === 'Mid-Level' && <div className="absolute -top-4 -right-4 w-20 h-20 drop-shadow-lg" data-id="element-156">
                <div className="relative w-full h-full rounded-full bg-orange-500/10 border-2 border-orange-500 flex items-center justify-center" data-id="element-157">
                  <img src="https://cdn.magicpatterns.com/uploads/o7Ft6KRxqTGN2gyzvBjeBs/Mid_Laravel_Developer_(1).png" alt="Mid-Level Laravel Developer Badge" className="w-16 h-16 object-contain" data-id="element-158" />
                </div>
              </div>}

            {activeTab === 'Senior' && <div className="absolute -top-4 -right-4 w-20 h-20 drop-shadow-lg" data-id="element-159">
                <div className="relative w-full h-full rounded-full bg-laravel-senior/10 border-2 border-laravel-senior flex items-center justify-center" data-id="element-160">
                  <img src="https://cdn.magicpatterns.com/uploads/4KYHxvP9f9tP7X3j65YDmQ/Senior_laravel_Developer.png" alt="Senior Laravel Developer Badge" className="w-16 h-16 object-contain" data-id="element-161" />
                </div>
              </div>}

            {activeTab === 'Artisan' && <div className="absolute -top-4 -right-4 w-20 h-20 drop-shadow-lg" data-id="element-162">
                <div className="relative w-full h-full rounded-full bg-laravel-artisan/10 border-2 border-laravel-artisan flex items-center justify-center" data-id="element-163">
                  <img src="https://cdn.magicpatterns.com/uploads/1SzESzxZdLN8TjEvpJKVcD/Artisan_Laravel_Developer.png" alt="Artisan Laravel Developer Badge" className="w-16 h-16 object-contain" data-id="element-164" />
                </div>
              </div>}

            <div className="flex items-center gap-2 mb-4" data-id="element-165">
              {card.icon}
              <span className={`font-bold ${activeData.accentColor}`} data-id="element-166">
                {activeTab === 'Artisan' ? <span className="flex flex-col leading-tight text-sm" data-id="element-167">
                    <span className="text-gray-500" data-id="element-168">Senior +</span>
                    <span data-id="element-169">Artisan</span>
                  </span> : activeTab}
              </span>
            </div>

            <h3 className="text-2xl font-display font-bold mb-4 min-h-[64px] text-gray-900" data-id="element-170">
              {card.title}
            </h3>

            <div className="flex items-end gap-3 mb-6" data-id="element-171">
              <span className="text-5xl font-display font-bold text-gray-900" data-id="element-172">
                ${card.price}
              </span>
              {card.originalPrice && <span className="text-gray-400 line-through text-lg mb-1" data-id="element-173">
                  ${card.originalPrice}
                </span>}
              {card.discount && <span className={`text-xs font-bold px-2 py-1 rounded-md mb-2 border border-current opacity-90 ${activeData.accentColor} bg-opacity-10`} data-id="element-174">
                  {card.discount}
                </span>}
            </div>

            <a href={card.buyLink || '#'} target="_blank" rel="noopener noreferrer" className={`w-full py-3 px-4 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors mb-8
              ${card.bestValue ? getButtonColor(activeTab) : getOutlineButtonColor(activeTab)}
            `} data-id="element-175">
              Buy Now <span className="text-lg leading-none" data-id="element-176">→</span>
            </a>

            <ul className="space-y-4 mb-8 flex-grow" data-id="element-177">
              {card.features.map((feature, i) => <li key={i} className="flex items-start gap-3 text-sm text-gray-600" data-id="element-178">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${getCheckmarkBg(activeTab)}`} data-id="element-179">
                    <CheckCircle2 className="w-3 h-3 text-white" data-id="element-180" />
                  </div>
                  <span className="flex-1 font-medium" data-id="element-181">{feature.name}</span>
                </li>)}
            </ul>
          </div>)}
        {activeData.cards.length === 0 && <div className="text-center text-gray-500 py-20 w-full" data-id="element-182">
            Pricing details coming soon.
          </div>}
      </div>

      {/* Artisan contact notice */}
      {activeTab === 'Artisan' && <div className="mt-10 text-center max-w-2xl mx-auto" data-id="element-183">
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-184">
            Already have a Senior Laravel Certification? Contact us for a link
            to purchase <strong className="text-gray-900" data-id="element-185">Artisan Only</strong>{' '}
            exam or{' '}
            <strong className="text-gray-900" data-id="element-186">
              Artisan Exam + Preparation
            </strong>{' '}
            bundle.
          </p>
          <a href="mailto:contact@certificationforlaravel.org" className="inline-flex items-center gap-2 px-6 py-3 bg-laravel-red text-white rounded-xl font-bold text-sm hover:bg-red-600 transition-colors" data-id="element-187">
            Contact Us
          </a>
        </div>}
    </div>;
}